import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "../components/ui/sheet"

export function SheetRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: Sheet root is a portal-emitting provider.
  // Surface atom contract on a wrapping span.
  // Note: `element.variant` is overloaded for both open-state ("open") and
  // side placement (top/bottom/left/right); data-variant carries the raw
  // token, compatible with any of these semantic uses.
  const atomDefaults = useAtomDefaults("sheet")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const isOpen = element.variant === "open" || element.visible === true || element.visible === "true"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  const side = (() => {
    const v = element.variant
    if (v === "top" || v === "bottom" || v === "left" || v === "right") return v
    return "bottom"
  })()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatchEvent(element.id, "close", {})
      }
    },
    [element.id, dispatchEvent],
  )

  return (
    <span
      data-atom-type="sheet"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-testid={`sheet-${element.id}`}
    >
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side={side}
          className={cn(
            element.className,
          )}
          data-testid={`sheet-content-${element.id}`}
        >
          {text && (
            <SheetTitle>
              {text}
            </SheetTitle>
          )}
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </SheetContent>
      </Sheet>
    </span>
  )
}
